import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import { useNotification } from 'hooks/useNotification';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';
import ApiClientManager from 'core/apiClient';
import { realmMigrationQueryKeys } from 'RealmModule/query/realmMigrationQueryKeys';
import {
  MigrationPreflightPayload,
  MigrationPreflightReport,
  RunRealmMigrationPayload,
  RunRealmMigrationResponse,
  migrationPreflight,
  runRealmMigration,
} from 'RealmModule/services/realmMigrationApi';

/** Shared so in-flight state survives this component unmounting. */
export const START_MIGRATION_MUTATION_KEY = ['realmMigration', 'start'];

/**
 * Run the read-only pre-flight.
 *
 * A mutation rather than a query because the operator triggers it deliberately — it is
 * not something that should re-run on focus or refetch, and its result is a snapshot the
 * confirmation step is then bound to.
 */
export const useMigrationPreflight = () => {
  const token = useSelector(selectAuthToken);
  const { showError } = useNotification();

  const mutation = useMutation<
    AxiosResponse<MigrationPreflightReport>,
    AxiosResponse<{ message?: string }>,
    MigrationPreflightPayload
  >({
    mutationFn: async (params) => {
      if (!token) {
        throw new Error('No auth token available');
      }
      return migrationPreflight({
        params,
        token,
        // Galaxy requires x-tenant to equal the body's sourceTenant, so the headers must
        // target the SOURCE realm rather than whatever the admin is currently viewing.
        config: {
          headers: ApiClientManager.buildUrlHeaders({
            tenantName: params.sourceTenant,
            realmId: params.sourceRealmId,
          }),
        },
      });
    },
    onError: (error) => showError(`Pre-flight failed: ${extractApiErrorMessage(error)}`),
  });

  return {
    runPreflight: mutation.mutate,
    report: mutation.data?.data,
    isRunning: mutation.isPending,
    reset: mutation.reset,
  };
};

export const useStartRealmMigration = ({
  onSuccess,
  onRefused,
  onIndeterminate,
}: {
  onSuccess?: VoidFunction;
  /**
   * Called when galaxy definitely did NOT start a migration.
   *
   * Two paths reach it, and both mean the server processed the request and declined:
   * the body-level refusal on a 2xx, and a 4xx. A 5xx or a transport failure does NOT —
   * there the request may have been applied and the caller must assume it was.
   */
  onRefused?: VoidFunction;
  /**
   * Called when the outcome is UNKNOWN — a 5xx or a transport failure.
   *
   * The distinction is not cosmetic: the request may have been applied, so the caller
   * must neither undo what it staged nor let the operator simply try again. A retry that
   * assumes nothing happened is how a running migration gets its side effects stripped by
   * the cleanup of the attempt that followed it.
   */
  onIndeterminate?: VoidFunction;
} = {}) => {
  const token = useSelector(selectAuthToken);
  const { showSuccess, showError, showWarning } = useNotification();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    AxiosResponse<RunRealmMigrationResponse>,
    AxiosResponse<{ message?: string }>,
    RunRealmMigrationPayload
  >({
    mutationKey: START_MIGRATION_MUTATION_KEY,
    mutationFn: async (params) => {
      if (!token) {
        throw new Error('No auth token available');
      }
      return runRealmMigration({
        params,
        token,
        config: {
          headers: ApiClientManager.buildUrlHeaders({
            tenantName: params.sourceTenant,
            realmId: params.sourceRealmId,
          }),
        },
      });
    },
    onSuccess: (response) => {
      // The multi-team refusal comes back as a 201 whose body carries only `message` and
      // no `status`, so a success HTTP code does NOT mean a migration started. Read the
      // body or we would tell the operator their migration is running when nothing ran.
      if (response?.data?.status !== 200) {
        showWarning(response?.data?.message ?? 'The migration was not started.', {
          persist: true,
        });
        onRefused?.();
        return;
      }

      // "Accepted", never "done": the API returns as soon as the traffic block is in
      // place and the data phase continues in the background for tens of minutes.
      showSuccess('Migration accepted. Watch its progress below — this takes 15-90 minutes.');
      onSuccess?.();
      // Returned so isPending stays true until the list reflects the new run.
      return queryClient.invalidateQueries({ queryKey: realmMigrationQueryKeys.lists() });
    },
    onError: (error) => {
      showError(`Could not start the migration: ${extractApiErrorMessage(error)}`);

      // `handleResponse` rejects a >=400 response with the raw AxiosResponse, so a 4xx —
      // "a migration is already in flight", a validation failure — arrives here rather
      // than at onSuccess. Nothing started, so it is as definite as the body-level
      // refusal. A 5xx or a transport failure carries no such guarantee: the migration
      // may be under way, and undoing its side effects there would be the destructive
      // mistake.
      const status = (error as { status?: number } | undefined)?.status;
      if (typeof status === 'number' && status >= 400 && status < 500) {
        onRefused?.();
      } else {
        onIndeterminate?.();
      }
    },
  });

  // Read from the mutation cache, not this hook's instance: a migration takes seconds to
  // return and the operator may navigate away from the page in that window. Keyed by the
  // shared mutationKey, so the button stays disabled wherever the form is remounted.
  const startsInFlight = useIsMutating({ mutationKey: START_MIGRATION_MUTATION_KEY });

  return {
    startMigration: mutation.mutate,
    isStarting: startsInFlight > 0,
  };
};
